import type {
  LearningDto,
  LearningEvidence,
  LearningOrigin,
} from '@/types/clu-memory';

/** A Memory tab a link can open; Company is where the page opens otherwise. */
export type MemoryTab = 'mine' | 'review';

/** The tab a link asks for; anything else opens Company. */
export const memoryTabOf = (value: unknown): MemoryTab | undefined =>
  value === 'mine' || value === 'review' ? value : undefined;

const counted = (value: number | undefined, one: string, many = `${one}s`) =>
  `${value ?? 0} ${value === 1 ? one : many}`;

/**
 * What an evidence learning rests on, in one line: "Last 30 days: 4 runs,
 * 6 contacted, 3 replies, 1 meeting, 1 bounce, 3 CRM records".
 */
export function evidenceLine(evidence: LearningEvidence): string {
  const seq = evidence.sequences ?? {};
  const parts = [
    counted(evidence.runs?.total, 'run'),
    `${seq.contacted ?? 0} contacted`,
    counted(seq.replied, 'reply', 'replies'),
    counted(seq.meeting, 'meeting'),
    counted(seq.bounced, 'bounce'),
  ];
  if (evidence.crm?.recordsWritten)
    parts.push(counted(evidence.crm.recordsWritten, 'CRM record'));
  const days = evidence.window?.days;
  return `${days ? `Last ${days} days` : 'Evidence'}: ${parts.join(', ')}`;
}

/** Text or a number as trimmed text; anything else as none. */
const textOf = (value: unknown): string =>
  typeof value === 'string'
    ? value.trim()
    : typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : '';

const quoted = (text: string) => `“${text}”`;

/**
 * What a Review row is called: the learning's label, or for a change to Clu
 * writing the change in one line: "Never say “touch base”", "Max words 90
 * (was 150)", "Acme: 3x meetings in 30 days". A value that is missing reads
 * as the text it would save.
 */
export function learningName(learning: LearningDto): string {
  const { kind, op, label, proposed } = learning;
  if (kind === 'proof') {
    const value =
      learning.value && typeof learning.value === 'object'
        ? learning.value
        : {};
    const customer = textOf(value.customer) || label;
    const result = textOf(value.result);
    return result && customer
      ? `${customer}: ${result}`
      : textOf(proposed) || label;
  }
  if (kind !== 'voice') return label;
  const value = textOf(learning.value) || textOf(proposed);
  if (!value) return label;
  switch (label) {
    case 'banned_phrase':
      return op === 'forget'
        ? `Allow ${quoted(value)}`
        : `Never say ${quoted(value)}`;
    case 'required_cta':
      return op === 'forget'
        ? `Stop ending with ${quoted(value)}`
        : `Always end with ${quoted(value)}`;
    case 'max_words': {
      const was = textOf(learning.beforeValue) || textOf(learning.before);
      return (
        `Max words ${value}` + (was && was !== value ? ` (was ${was})` : '')
      );
    }
    case 'style':
      return `Style: ${value}`;
    default:
      return value;
  }
}

const ORIGINS: Record<LearningOrigin, string> = {
  chat: 'From chat',
  review_edits: 'From your edits in Review',
  chat_pass: 'From past chats',
};

/** Where a learning came from, in a few words; null when the server names none. */
export const originLabel = (origin: unknown): string | null =>
  origin === 'chat' || origin === 'review_edits' || origin === 'chat_pass'
    ? ORIGINS[origin]
    : null;
